import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import React, { ComponentType, FC, Fragment, useEffect, useRef } from 'react';

import {
  addBreadcrumb,
  captureException,
  captureMessage,
  configureScope,
  withScope,
} from '@sentry/nextjs';
import { getCookies, setCookie } from 'cookies-next';
import {
  User,
  browserSessionPersistence,
  onAuthStateChanged,
  onIdTokenChanged,
  setPersistence,
  signInWithCustomToken,
} from 'firebase/auth';
import { create } from 'zustand';

import { CHAT_BOT_API } from '@api/chatbot';
import { Platform } from '@constants';
import { useWebView } from '@utils/webview';

import { api } from '../api';
import Loading from '../components/common/Loading';
import type { IFirebaseCustomToken } from '../model';
import { useAnalytics } from '../utils/analytics';
import { firebaseAuth } from '../utils/firebase';
import { useMe } from '../utils/hook';
import {
  getTokenFromCookies,
  isEnv,
  removeTokenCookies,
  retry,
} from '../utils/misc';
import { getRouterQuery } from '../utils/query';
import { useGlobalContext } from './global';

type AuthStatus = 'initial' | 'loading' | 'success' | 'error' | 'anonymous';

export type AuthStore = {
  status: AuthStatus;
  setStatus: (status: AuthStatus) => void;
};

export const useAuthStore = create<AuthStore>((set) => ({
  status: 'initial',
  setStatus: (status) => set({ status }),
}));

const fetchFirebaseToken = async (accessToken: string) => {
  const { data } = await api.get<IFirebaseCustomToken>('/users/me/firebase/', {
    headers: { Authorization: accessToken },
  });
  return data;
};

type Props = {
  children: React.ReactNode;
};

/** Firebase auth */
const authenticate = async (token: string) => {
  const { token: customToken } = await fetchFirebaseToken(token);

  await retry(() => signInWithCustomToken(firebaseAuth, customToken), 3);
};

const getToken = () => {
  const cookies = getCookies();
  return getTokenFromCookies(cookies);
};

const getIdToken = async (user?: User | null) => {
  const idToken = user ? await user.getIdToken() : undefined;
  return idToken;
};

function getTokenExp(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000; // ms
  } catch {
    return null;
  }
}

const MIN_REFRESH_INTERVAL = 5 * 60 * 1000; // 5분
let lastRefresh = 0;

export const AuthProvider: FC<Props> = ({ children }) => {
  const router = useRouter();
  const { status, setStatus } = useAuthStore();
  const { platform, version, isCheming } = useGlobalContext();
  const { web, listen } = useWebView();

  const { firebase } = useAnalytics();

  const { data: me } = useMe({ enabled: status === 'success' });

  // firebase event listener
  useEffect(() => {
    const interceptor = api.interceptors.request.use(async (config) => {
      const user = firebaseAuth.currentUser;
      const idToken = await getIdToken(user);

      config.headers = {
        ...config.headers,
        ...(idToken ? { Authorization: `FIT ${idToken}` } : undefined),
        'x-chatie-platform': `${platform}/${version}`,
      };

      return config;
    });

    const chatInterceptor = CHAT_BOT_API.interceptors.request.use(
      async (config) => {
        const user = firebaseAuth.currentUser;
        const idToken = await getIdToken(user);

        config.headers = {
          ...config.headers,
          ...(idToken ? { Authorization: `FIT ${idToken}` } : undefined),
          'x-chatie-platform': `${platform}/${version}`,
        };

        return config;
      }
    );

    setPersistence(firebaseAuth, browserSessionPersistence);

    const unsubscribeAuthStateChanged = onAuthStateChanged(
      firebaseAuth,
      (user) => {
        if (user) {
          setStatus('success');
        } else {
          setStatus('loading');
        }
      }
    );

    const unsubscribeIdTokenChanged = onIdTokenChanged(
      firebaseAuth,
      async (user) => {
        if (user) {
          const idToken = await getIdToken(user);
          setCookie('firebaseIdToken', idToken, {
            domain: isEnv('dev')
              ? undefined
              : isCheming
              ? 'cheming.ai'
              : 'chatie.me',
            expires: new Date(Date.now() + 1000 * 60 * 60),
          });
        }
      }
    );

    return () => {
      api.interceptors.request.eject(interceptor);
      CHAT_BOT_API.interceptors.request.eject(chatInterceptor);
      unsubscribeAuthStateChanged();
      unsubscribeIdTokenChanged();
    };
  }, []);

  // initial sign in with cookie
  useEffect(() => {
    const path = router.pathname;
    if (path === '/signout') {
      setStatus('anonymous');
      return;
    }

    if (status !== 'loading') {
      return;
    }

    // query parameter에 idToken 있을 경우, 로그인 시 사용
    const tokenFromQuery = router.query.id_token
      ? `FIT ${getRouterQuery(router.query.id_token) as string}`
      : undefined;

    const token = tokenFromQuery || getToken();
    if (!token) {
      if (platform === Platform.web || path?.includes('setting/info/terms')) {
        setStatus('anonymous');
      } else {
        addBreadcrumb({
          message: 'loading 설정 토큰X, FirebaseIdToken호출',
        });
        setStatus('loading');
        web.data('firebaseIdToken');
      }
      return;
    }

    authenticate(token).catch((error) => {
      const status = error?.response?.status ?? error?.status;

      if (platform === Platform.web) {
        setStatus('error');
        withScope((scope) => {
          scope.setExtra('reason', 'firebase-auth');
          captureException(error);
        });
        if (!status) {
          removeTokenCookies();
        }
        if (status < 500) {
          removeTokenCookies();
        } else {
          captureException(error);

          alert('서버에 장애가 발생하였습니다. 종료 후 다시 시도해주세요');
        }
      } else {
        addBreadcrumb({
          message: 'authenticate 실패 useEffect',
        });
        setStatus('loading');
        web.data('firebaseIdToken');
      }
    });
  }, [setStatus, status]);

  // set user for third-party
  useEffect(() => {
    if (!me) {
      return;
    }

    configureScope((scope) => {
      scope.setUser({
        id: me.id?.toString(),
        username: me.username,
        nickname: me.nickname,
        storyCount: me.storyCount,
        storyTapCount: me.storyTapCount,
        followerCount: me.followerCount,
        followingCount: me.followingCount,
      });
    });

    firebase.setUserProperty('chatie_uid', me.id);
  }, [me]);

  useEffect(() => {
    const unlisten = listen('data', (e) => {
      const { key, value } = e.detail;

      addBreadcrumb({
        message: 'token 수신',
      });
      if (key === 'firebaseIdToken' && value.idToken) {
        const token = `FIT ${value.idToken}`;
        authenticate(token).catch((error) => {
          const status = error?.response?.status ?? error?.status;
          addBreadcrumb({
            message: 'authhenticate 실패 listener',
            category: 'native',
            level: 'info',
            data: {
              key,
              hasValue: !!value,
              hasIdToken: !!(key === 'firebaseIdToken' && value?.idToken),
              status, // ✅ 직접 조회한 상태 사용
              timestamp: new Date().toISOString(),
            },
          });
          setStatus('error');
          withScope((scope) => {
            scope.setExtra('reason', 'firebase-auth');
            captureException(error);
          });
          if (!status) {
            removeTokenCookies();
          }
          if (status < 500) {
            removeTokenCookies();
          } else {
            captureException(error);
            alert('서버에 장애가 발생하였습니다. 종료 후 다시 시도해주세요');
          }
        });
      } else {
        captureMessage('Native 토큰 데이터 수신 실패', {
          level: 'error', // 'debug' | 'info' | 'warning' | 'error' | 'fatal'
          tags: {
            key: 'firebaseIdToken',
            source: 'native',
          },
          extra: {
            hasIdToken: false,
            status,
            timestamp: new Date().toISOString(),
          },
        });

        setStatus('error');
      }
    });

    return () => {
      unlisten();
    };
  }, [listen]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && firebaseAuth.currentUser) {
        const idToken = await firebaseAuth.currentUser.getIdToken();
        const exp = getTokenExp(idToken);
        const now = Date.now();
        if (
          exp &&
          exp - now < MIN_REFRESH_INTERVAL && // 만료 5분 이하 남았을 때만
          now - lastRefresh > MIN_REFRESH_INTERVAL // 최소 5분 간격
        ) {
          lastRefresh = now;
          const newToken = await firebaseAuth.currentUser.getIdToken(true);
          setCookie('firebaseIdToken', newToken, {
            domain: isEnv('dev')
              ? undefined
              : isCheming
              ? 'cheming.ai'
              : 'chatie.me',
            secure: !isEnv('dev'),
            path: '/',
            expires: new Date(Date.now() + 1000 * 60 * 60),
          });
        }
      }
    };
    const handleFocus = async () => {
      if (firebaseAuth.currentUser) {
        const idToken = await firebaseAuth.currentUser.getIdToken();
        const exp = getTokenExp(idToken);
        const now = Date.now();
        if (
          exp &&
          exp - now < MIN_REFRESH_INTERVAL && // 만료 5분 이하 남았을 때만
          now - lastRefresh > MIN_REFRESH_INTERVAL // 최소 5분 간격
        ) {
          lastRefresh = now;
          const newToken = await firebaseAuth.currentUser.getIdToken(true);
          setCookie('firebaseIdToken', newToken, {
            domain: isEnv('dev')
              ? undefined
              : isCheming
              ? 'cheming.ai'
              : 'chatie.me',
            secure: !isEnv('dev'),
            path: '/',
            expires: new Date(Date.now() + 1000 * 60 * 60),
          });
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  return <Fragment key="auth-context-provider">{children}</Fragment>;
};

type WrappedComponentOptions = {
  isSignInRequired?: boolean;
  LoadingComponent?: ComponentType;
};

export function withAuth<P extends object>(
  Component: ComponentType<P>,
  { isSignInRequired = true, LoadingComponent }: WrappedComponentOptions = {}
) {
  const WrappedComponent = (props: P) => {
    const { t } = useTranslation();
    const { status } = useAuthStore();
    const { platform } = useGlobalContext();
    const router = useRouter();
    const isPending = status === 'initial' || status === 'loading';

    useEffect(() => {
      if (isPending) {
        return;
      }

      if ((status === 'error' || status === 'anonymous') && isSignInRequired) {
        if (platform === Platform.web) {
          router.replace('/signin');
        } else {
          alert(t('로그인이 필요한 서비스입니다. 다시 시도해주세요.'));
        }
      }
    }, [status]);

    if ((isSignInRequired && status !== 'success') || isPending) {
      return LoadingComponent ? <LoadingComponent /> : <Loading fixed />;
    }

    return <Component {...props} />;
  };
  return WrappedComponent;
}
